import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import TabBar from '../../../../component/tab-bar/tab-bar';
import globalUserProfile from '../../../../stores/global-user-profile';
import { LectureLecturer } from '../../../../api/types/lecturers/lectures/lecture-lecturer';
import { UserType } from '../../../../api/types/user/user-type';
import { StartLectureAttendanceDialog } from '../../../../pages/course/lectures/list-view/components/start-lecture-attendance-dialog';
import { EditTextDialog } from '../../../../pages/settings/componenets/edit-entity-name-dialog/edit-text-dialog';
import { updateLectureNameProfessorOperation } from './operations/update-lecture-name-professor-operation';
import { MyLectureStartDialog } from './components/my-lecture-start-dialog';
import { MyLectureFinishDialog } from './components/my-lecture-finish-dialog';
import { MyLectureActions } from './components/my-lecture-actions';
import { getCommonColumns } from './components/my-lecture-columns';
import { fetchMyLecturesOperation } from './operations/fetch-my-lectures-operation';
import { ProfessorProfile } from '../../../../api/types/settings/settings-all';

export const MyLectures = observer(() => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [allLectures, setAllLectures] = useState<LectureLecturer[]>([]);
  const [isLoadingLectures, setIsLoadingLectures] = useState(false);
  const latestRequestSeqRef = useRef(0);
  const [showUpdateAttendanceDialog, setShowUpdateAttendanceDialog] =
    useState(false);
  const [selectedLectureForUpdate, setSelectedLectureForUpdate] =
    useState<LectureLecturer | null>(null);
  const [showStartConfirmDialog, setShowStartConfirmDialog] = useState(false);
  const [selectedLectureForStart, setSelectedLectureForStart] =
    useState<LectureLecturer | null>(null);
  const [isStartingLecture, setIsStartingLecture] = useState(false);
  const [showFinishConfirmDialog, setShowFinishConfirmDialog] = useState(false);
  const [selectedLectureForFinish, setSelectedLectureForFinish] =
    useState<LectureLecturer | null>(null);
  const [isFinishingLecture, setIsFinishingLecture] = useState(false);
  const [editLectureNameDialogId, setEditLectureNameDialogId] = useState<
    number | undefined
  >(undefined);
  const [editLectureName, setEditLectureName] = useState<string>('');
  const [selectedLectureForEdit, setSelectedLectureForEdit] =
    useState<LectureLecturer | null>(null);
  const [isUpdatingLectureName, setIsUpdatingLectureName] = useState(false);

  const getProfessorId = (): number | null => {
    if (!globalUserProfile.userProfile) return null;
    if (globalUserProfile.userProfile.userType !== UserType.LECTURER)
      return null;
    return globalUserProfile.userProfile.userWithRole?.id ?? null;
  };

  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const endOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

  const getFiltersForTab = () => {
    const now = new Date();
    switch (selectedTabIndex) {
      // הרצאות שקרו
      case 0:
        return {
          startDate: new Date(
            now.getFullYear() - 1,
            now.getMonth(),
            now.getDate(),
          ),
          endDate: now,
        };
      // הרצאות היום
      case 1:
        return { startDate: startOfDay(now), endDate: endOfDay(now) };
      // הרצאות חריגות
      case 2:
        return { startDate: null, endDate: now };
      // הרצאות עתידיות
      case 3:
        return { startDate: now, endDate: null };
      default:
        return { startDate: null, endDate: null };
    }
  };

  useEffect(() => {
    const professorId = getProfessorId();
    if (!professorId) {
      setAllLectures([]);
      return;
    }

    const requestSeq = ++latestRequestSeqRef.current;
    const { startDate, endDate } = getFiltersForTab();
    fetchMyLecturesOperation(
      professorId,
      {
        offset: 0,
        limit: 500,
        startDate,
        endDate,
      },
      (loading) => {
        if (latestRequestSeqRef.current !== requestSeq) return;
        setIsLoadingLectures(loading);
      },
      (rows) => {
        if (latestRequestSeqRef.current !== requestSeq) return;
        setAllLectures(rows);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTabIndex, globalUserProfile.userProfile]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    console.debug('[MyLectures] state snapshot', {
      selectedTabIndex,
      isLoadingLectures,
      allLecturesCount: allLectures.length,
      firstLecture: allLectures[0]
        ? {
            id: allLectures[0].id,
            name: allLectures[0].name,
            startTime: allLectures[0].startTime,
            endTime: allLectures[0].endTime,
            actualStartTime: allLectures[0].actualStartTime,
            actualEndTime: allLectures[0].actualEndTime,
            programId: allLectures[0].programId,
            courseId: allLectures[0].courseId,
          }
        : null,
    });
  }, [allLectures, isLoadingLectures, selectedTabIndex]);

  // Helper function to check if a date is today
  const isToday = (date: Date): boolean => {
    const today = new Date();
    const targetDate = new Date(date);
    return (
      today.getFullYear() === targetDate.getFullYear() &&
      today.getMonth() === targetDate.getMonth() &&
      today.getDate() === targetDate.getDate()
    );
  };

  // Helper function to check if a date is in the past (not today)
  const isPast = (date: Date): boolean => {
    const today = new Date();
    const todayStart = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    return new Date(date) < todayStart;
  };

  // Helper function to check if a date is in the future (not today)
  const isFuture = (date: Date): boolean => {
    const today = new Date();
    const todayEnd = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + 1,
    );
    return new Date(date) >= todayEnd;
  };

  // Filter lectures by categories
  const getTodayLectures = (): LectureLecturer[] => {
    const userProfile = globalUserProfile.userProfile;
    const profileLectures =
      userProfile &&
      userProfile.userType === UserType.LECTURER &&
      (userProfile.profile as ProfessorProfile | undefined)?.lectures;

    const sourceLectures: LectureLecturer[] =
      Array.isArray(profileLectures) && profileLectures.length > 0
        ? (profileLectures as unknown as LectureLecturer[])
        : allLectures;

    return sourceLectures.filter((lecture) =>
      isToday(new Date(lecture.startTime)),
    );
  };

  const getPastLectures = (): LectureLecturer[] => {
    return allLectures
      .filter((lecture) => {
        const lectureDate = new Date(lecture.startTime);
        const finishedByStatus =
          (lecture as unknown as { lectureStatus?: string }).lectureStatus ===
          'FINISHED';
        return isPast(lectureDate) || finishedByStatus;
      })
      .sort(
        (a, b) =>
          new Date(b.startTime).getTime() - new Date(a.startTime).getTime(),
      );
  };

  const getIrregularLectures = (): LectureLecturer[] => {
    return allLectures
      .filter((lecture) => {
        const lectureDate = new Date(lecture.startTime);
        const isNotToday = !isToday(lectureDate);

        return (
          (isPast(lectureDate) && lecture.actualStartTime === null) ||
          (lecture.actualStartTime !== null &&
            lecture.actualEndTime === null &&
            isNotToday)
        );
      })
      .sort(
        (a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
      );
  };

  const getFutureLectures = (): LectureLecturer[] => {
    return allLectures
      .filter((lecture) => isFuture(new Date(lecture.startTime)))
      .sort(
        (a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
      );
  };

  const todayLectures = getTodayLectures();
  const pastLectures = getPastLectures();
  const irregularLectures = getIrregularLectures();
  const futureLectures = getFutureLectures();

  const tabs = [
    'הרצאות היסטוריות',
    'הרצאות היום',
    'הרצאות חריגות',
    'הרצאות עתידיות',
  ];

  const getCountForSelectedTab = () => {
    switch (selectedTabIndex) {
      case 0:
        return pastLectures.length;
      case 1:
        return todayLectures.length;
      case 2:
        return irregularLectures.length;
      case 3:
        return futureLectures.length;
      default:
        return 0;
    }
  };

  const getLabelForSelectedTab = () => {
    return tabs[selectedTabIndex] ?? '';
  };

  // Template for status indicator (for today's lectures)
  const statusTemplate = (rowData: LectureLecturer) => {
    if (rowData.actualStartTime !== null && rowData.actualEndTime !== null) {
      return <Tag severity="success" value="הסתיימה" />;
    } else if (rowData.actualStartTime !== null) {
      return <Tag severity="warning" value="החלה" />;
    }
    return <Tag severity="info" value="טרם החלה" />;
  };

  // Handle start lecture - open confirmation dialog first
  const handleStartLecture = (lecture: LectureLecturer) => {
    setSelectedLectureForStart(lecture);
    setShowStartConfirmDialog(true);
  };

  // Handle finish lecture - open confirmation dialog first
  const handleFinishLecture = (lecture: LectureLecturer) => {
    setSelectedLectureForFinish(lecture);
    setShowFinishConfirmDialog(true);
  };

  // Handle update attendance - open dialog
  const handleUpdateAttendance = (lecture: LectureLecturer) => {
    setSelectedLectureForUpdate(lecture);
    setShowUpdateAttendanceDialog(true);
  };

  // Handle edit lecture name - open dialog
  const handleEditLectureName = (lecture: LectureLecturer) => {
    setSelectedLectureForEdit(lecture);
    setEditLectureName(lecture.name || '');
    setEditLectureNameDialogId(lecture.id);
  };

  // Handle save lecture name
  const handleSaveLectureName = async (id: number, newName: string) => {
    if (!selectedLectureForEdit) return;

    await updateLectureNameProfessorOperation(
      selectedLectureForEdit.programId,
      selectedLectureForEdit.courseId,
      selectedLectureForEdit.id,
      newName,
      setIsUpdatingLectureName,
    );

    setEditLectureNameDialogId(undefined);
    setSelectedLectureForEdit(null);
    setEditLectureName('');
  };

  // Handle hide edit dialog
  const handleHideEditDialog = () => {
    setEditLectureNameDialogId(undefined);
    setSelectedLectureForEdit(null);
    setEditLectureName('');
  };

  // Template for action buttons (only for today's lectures)
  const actionTemplate = (rowData: LectureLecturer) => {
    return (
      <MyLectureActions
        lecture={rowData}
        onStartLecture={handleStartLecture}
        onFinishLecture={handleFinishLecture}
        onUpdateAttendance={handleUpdateAttendance}
      />
    );
  };

  // Column configuration props
  const columnProps = {
    onEditLectureName: handleEditLectureName,
  };

  // Column configuration props for past lectures (no edit allowed)
  const pastLecturesColumnProps = {
    onEditLectureName: handleEditLectureName,
    allowEditName: false,
  };

  const getTitleForSelectedTab = () => {
    switch (selectedTabIndex) {
      case 0:
        return `הרצאות שקרו`;
      case 1:
        return `הרצאות היום`;
      case 2:
        return `הרצאות חריגות`;
      case 3:
        return `הרצאות עתידיות`;
      default:
        return '';
    }
  };

  const getSelectedTabLectures = (): LectureLecturer[] => {
    switch (selectedTabIndex) {
      case 0:
        return pastLectures;
      case 1:
        return todayLectures;
      case 2:
        return irregularLectures;
      case 3:
        return futureLectures;
      default:
        return [];
    }
  };

  const getEmptyMessageForSelectedTab = () => {
    switch (selectedTabIndex) {
      case 0:
        return <div style={{ textAlign: 'right' }}>אין הרצאות קודמות</div>;
      case 1:
        return <div style={{ textAlign: 'right' }}>אין הרצאות היום</div>;
      case 2:
        return <div style={{ textAlign: 'right' }}>אין הרצאות חריגות</div>;
      case 3:
        return <div style={{ textAlign: 'right' }}>אין הרצאות עתידיות</div>;
      default:
        return <div style={{ textAlign: 'right' }} />;
    }
  };

  const getColumnsForSelectedTab = () => {
    if (selectedTabIndex === 0) {
      return getCommonColumns(pastLecturesColumnProps);
    }

    if (selectedTabIndex === 1) {
      return [
        ...getCommonColumns(columnProps, false),
        <Column
          key="status"
          header="סטטוס"
          body={statusTemplate}
          style={{ textAlign: 'center', minWidth: '100px' }}
          headerStyle={{ textAlign: 'center' }}
        />,
        <Column
          key="actions"
          header="פעולות"
          body={actionTemplate}
          style={{ textAlign: 'center', minWidth: '150px' }}
          headerStyle={{ textAlign: 'center' }}
        />,
      ];
    }

    return getCommonColumns(columnProps);
  };

  const getScrollHeightForSelectedTab = () => {
    if (selectedTabIndex === 3) return '100%';
    return '600px';
  };

  return (
    <div style={{ padding: '20px', width: '100%' }}>
      <div style={{ direction: 'rtl', marginBottom: '18px' }}>
        <div
          style={{
            color: '#041E49',
            fontSize: '34px',
            fontWeight: 800,
            lineHeight: 1.1,
            marginBottom: '6px',
          }}
        >
          ההרצאות שלי
        </div>
        <div style={{ color: '#5B6B88', fontSize: '14px' }}>
          צפייה, ניהול נוכחות ועדכון שמות מפגשים
        </div>
      </div>

      {/* Start Lecture Confirmation Dialog */}
      <MyLectureStartDialog
        visible={showStartConfirmDialog}
        selectedLecture={selectedLectureForStart}
        isLoading={isStartingLecture}
        setIsLoading={setIsStartingLecture}
        onHide={() => {
          setShowStartConfirmDialog(false);
          setSelectedLectureForStart(null);
        }}
      />

      {/* Finish Lecture Confirmation Dialog */}
      <MyLectureFinishDialog
        visible={showFinishConfirmDialog}
        selectedLecture={selectedLectureForFinish}
        isLoading={isFinishingLecture}
        setIsLoading={setIsFinishingLecture}
        onHide={() => {
          setShowFinishConfirmDialog(false);
          setSelectedLectureForFinish(null);
        }}
      />

      {/* Edit Lecture Name Dialog */}
      <EditTextDialog
        id={editLectureNameDialogId}
        value={editLectureName || ' '}
        title="עריכת שם המפגש"
        label="נא להזין את שם המפגש"
        loading={isUpdatingLectureName}
        onSave={handleSaveLectureName}
        onHide={handleHideEditDialog}
      />

      {/* Update Attendance Dialog */}
      <StartLectureAttendanceDialog
        visible={showUpdateAttendanceDialog}
        lecture={null}
        lectureInfo={
          selectedLectureForUpdate
            ? {
                programId: selectedLectureForUpdate.programId,
                courseId: selectedLectureForUpdate.courseId,
                lectureId: selectedLectureForUpdate.id,
              }
            : undefined
        }
        useProfessorMode={true}
        updateAttendanceOnly={true}
        onHide={() => {
          setShowUpdateAttendanceDialog(false);
          setSelectedLectureForUpdate(null);
        }}
      />

      <div style={{ marginBottom: '20px' }}>
        <TabBar
          tabs={tabs}
          activeIndex={selectedTabIndex}
          onClick={setSelectedTabIndex}
        />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          direction: 'rtl',
          marginBottom: '14px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ color: '#041E49', fontSize: '26px', fontWeight: 800 }}>
            {getTitleForSelectedTab()}
          </div>
          <Tag
            value={`${getCountForSelectedTab()}`}
            severity={
              selectedTabIndex === 1
                ? 'info'
                : selectedTabIndex === 2
                  ? 'warning'
                  : selectedTabIndex === 0
                    ? 'success'
                    : 'info'
            }
            style={{
              padding: '6px 10px',
              borderRadius: '999px',
              fontWeight: 700,
            }}
          />
        </div>
        <div style={{ color: '#5B6B88', fontSize: '13px' }}>
          {getLabelForSelectedTab()}
        </div>
      </div>

      <DataTable
        key={selectedTabIndex}
        value={getSelectedTabLectures()}
        emptyMessage={getEmptyMessageForSelectedTab()}
        loading={isLoadingLectures}
        rowHover
        dataKey="id"
        scrollable
        scrollHeight={getScrollHeightForSelectedTab()}
        tableStyle={{
          direction: 'rtl',
        }}
        className="p-datatable-striped"
      >
        {getColumnsForSelectedTab()}
      </DataTable>
    </div>
  );
});
